import type { GameId } from '@tg/shared';
import type { ArtTheme } from '../covers/kit';
import type { GameScene, SceneVariant } from './types';

/*
 * Registry of game scenes (game-screen material, src/material/material.ts). Every `art/scenes/<game>.ts` default-exports
 * `defineScene({ game, scene, accent })` and is picked up here by the glob: adding a game touches no shared list
 * (README.md). A raster `scene` from pnpm art:process (raster.ts slotBackground) is layered over the vector one by CSS.
 */
export type { GameScene, SceneVariant };

const modules = import.meta.glob<GameScene>(['./*.ts', '!./*.test.ts', '!./index.ts', '!./types.ts'], {
  eager: true,
  import: 'default',
});

/** file name → scene (for the test: the file is named after its game) */
export const SCENE_FILES: Readonly<Record<string, GameScene>> = Object.fromEntries(
  Object.entries(modules).map(([path, s]) => [path.replace(/^.*\/|\.ts$/g, ''), s]),
);

const SCENES = new Map<GameId, GameScene>(Object.values(SCENE_FILES).map((s) => [s.game, s]));

/** The games that have a scene, i.e. a material (sorted by file name). */
export const SCENE_GAMES: readonly GameId[] = [...SCENES.keys()];

export const hasScene = (game: GameId): boolean => SCENES.has(game);

const get = (game: GameId): GameScene => {
  const s = SCENES.get(game);
  if (!s) throw new RangeError(`no scene for ${game}`);
  return s;
};

export const sceneSvg = (game: GameId, theme: ArtTheme, v: SceneVariant): string => get(game).scene(theme, v);
export const accentSvg = (game: GameId, theme: ArtTheme): string => get(game).accent(theme);

const cache = new Map<string, string>();
const uri = (key: string, make: () => string): string => {
  let u = cache.get(key);
  if (!u) {
    u = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(make())}`;
    cache.set(key, u);
  }
  return u;
};

/** Data URI of a scene (cached per game / theme / variant). */
export const sceneUri = (game: GameId, theme: ArtTheme, v: SceneVariant): string =>
  uri(`${game}:${theme}:${v}`, () => sceneSvg(game, theme, v));
/** Data URI of the header accent tile. */
export const accentUri = (game: GameId, theme: ArtTheme): string =>
  uri(`${game}:${theme}:accent`, () => accentSvg(game, theme));
